import React, { useEffect, useState } from 'react';
import { View, Image, FlatList, ActivityIndicator } from 'react-native';
import { Icon } from 'react-native-elements';
import { useDispatch, useSelector } from 'react-redux';
import Colors from '../../../constants/Colorsv2';
import { Text, TouchableOpacity } from '../../../globalComponents/NativeOverrides';
import { analytics_getLeaders } from '../../../store/actionsv1/analytics';
import { player_getBulkPlayers } from '../../../store/actionsv1/player';
import { CategoryProps, LeaderProps, TimerangeProps } from '../../../store/types/analytics_svc';
import { PublicPlayerProps } from '../../../store/types/auth_svc';
import { formatPlace } from '../helpers/leaderboard';

type LeaderboardCardProps = {
    category_id:string,
    timerange_id:string,
    viewer_id?:string,
    show_limit?:number,
    onLeaderSelect:(leader:LeaderProps) => void
}

const LeaderboardCard = ({ category_id, timerange_id, viewer_id, show_limit, onLeaderSelect }:LeaderboardCardProps) => {
    const [ loading, setLoading ] = useState(false);
    const [ offset, setOffset ] = useState(0);

    const dispatch = useDispatch();
    const leaders:LeaderProps[] = useSelector((state:any) => state.analytics.leaders.filter(l => l.category_id == category_id && l.timerange_id == timerange_id && l.leaderboard_eligible), (left, right) => {
        if(left.length != right.length){ return false }
        if(left[0]?.category_id != right[0]?.category_id){ return false }
        if(left[0]?.timerange_id != right[0]?.timerange_id){ return false }
        return true

    })
    const stored_players:PublicPlayerProps[] = useSelector((state:any) => state.social.stored_players, (left, right) => left.length === right.length)
    const category:CategoryProps | undefined = useSelector((state:any) => state.analytics.stored_categories.find((c:CategoryProps) => c.category_id == category_id), (left, right) => left?.category_id == right?.category_id)
    const timerange: TimerangeProps | undefined = useSelector((state:any) => state.analytics.stored_timeranges.find((c:TimerangeProps) => c.timerange_id == timerange_id), (left, right) => left?.timerange_id == right?.timerange_id)
    let filtered_leaders = leaders
    if(show_limit){
        filtered_leaders = filtered_leaders.slice(0, show_limit)
    }
    
    useEffect(() => {
        if(!category_id || !timerange_id){ return }
        setOffset(0)
        getLeadersFromServer(0)
    },[category_id, timerange_id])

    const getLeadersFromServer = async(offset:number) => {
        setLoading(true)
        await dispatch(analytics_getLeaders(category_id, timerange_id, offset, viewer_id))
        setLoading(false)
    }

    const handleShowMore = () => {
        getLeadersFromServer(offset + 1)
        setOffset(offset+1)
    }

    useEffect(() => {
        if(leaders.length === 0){ return }
        dispatch(player_getBulkPlayers(leaders.map(l => l.player_id)))
    },[leaders.length])

    const renderLeaders = (data: {item:LeaderProps, index:number}) => {
        const player = stored_players.find(sp => sp.player_id == data.item.player_id)
        if(!category){ return <></> }
        if(!player){ return<></> }
        return (
            <LeaderCard leader={data.item} category={category} player={player} onSelect={(leader:LeaderProps) => onLeaderSelect(leader)}/>
        )
    }

    return (
        <View>
            {category && timerange ?
            <View style={{ margin:10 }}>
                <Text size={14} color={Colors.brand.midnight} weight='semibold' textAlign='center'>{category.name} - {timerange.name}</Text>
            </View>
            :<></>}
            <FlatList data={filtered_leaders.sort((a,b) => a.place - b.place)} renderItem={renderLeaders} keyExtractor={(item) => item.leader_id.toString()} />
            {loading ? 
            <View style={{ padding:10, justifyContent:'center', alignItems:'center' }}>
                <ActivityIndicator size='large' color={Colors.brand.midnight}/>
            </View>
            :<></>}
            {!show_limit ?
            <TouchableOpacity style={{margin:20, padding:10, backgroundColor:Colors.shades.shade100, borderRadius:100}} onPress={() => handleShowMore()}>
                <Text size={14} color={Colors.brand.electric} weight='semibold' textAlign='center'>Load More</Text>
            </TouchableOpacity>
            :<></>}
        </View>
    )

}

export default LeaderboardCard


type LeaderCardProps = {
    leader:LeaderProps,
    player:PublicPlayerProps,
    category:CategoryProps,
    onSelect:(leader:LeaderProps) => void
}
export const LeaderCard = ({ leader, player, category, onSelect }:LeaderCardProps) => {

    let pic = player.profile_pic
    if(!pic || pic == ''){
        pic = require('../../../assets/images/default-avatar.png')
    }

    return (
        <TouchableOpacity onPress={() => onSelect(leader)}>
        <View style={{flexDirection:'row', alignItems:'center', margin:16, marginTop:4, marginBottom:4, borderRadius:32, padding:20, paddingTop:8, paddingBottom:8, backgroundColor:Colors.shades.shade200}}>
                {leader.leaderboard_eligible ?
                <View style={{flex:1/7}}>
                    <Text size={14} weight='semibold' textAlign='center' color={Colors.brand.midnight}>{formatPlace(leader.place)}</Text>
                    <Icon name='chevron-up' type='feather' color={Colors.highlights.highlight400} size={8}/>
                </View>
                :
                <View style={{flex:1/7}}>
                    <Icon name='lock' type='feather' color={Colors.brand.slate} size={18}/>
                </View>
                }
                <View style={{flex:1/7}}>
                    <Image style={{height:32, width:32, borderRadius:100}} source={pic} />
                </View>
                <View style={{flex:4/7}}>
                    <View style={{flexDirection:'row', alignItems:'center'}}>
                        <Text  size={14} weight='semibold'  color={Colors.brand.midnight}>{player.first_name} {player.last_name}</Text>
                        {leader.place === 1 ?
                            <Image style={{marginLeft:8, height:12, width:15}} source={require('../../../assets/images/icon-crown.png')} />
                        :<></>}
                    </View>
                    <Text size={12} color={Colors.brand.slate} weight='regular'>@{player.username}</Text>
                    {!leader.leaderboard_eligible && leader.not_eligible_reason ?
                    <Text color={Colors.utility.error} size={12} style={{ paddingBottom:2, paddingTop:2 }}>{leader.not_eligible_reason}</Text>
                    :<></>}
                </View>
                <View style={{flex:1/5}}>
                    <Text size={14} weight='regular' textAlign='right' color={Colors.brand.electric}>{category?.stat_label_loc==='prefix'?category.stat_label:''}{leader.category_score.toFixed(2)}{category?.stat_label_loc==='suffix'?category.stat_label:''}</Text>
                </View>
            </View>
        </TouchableOpacity>
    )
}